/**
 * Offline Sync Service für Feld-Modus
 * 
 * Synchronisiert ausstehende Uploads wenn wieder online.
 */

import { 
  getPendingUploads, 
  updateUploadStatus, 
  deleteUpload,
  getAllPendingUploads 
} from './offlineStorage';
import { getApiUrl } from './api';

let syncInProgress = false;
let syncListeners = [];

/**
 * Registriert einen Listener für Sync-Events
 */
export const addSyncListener = (callback) => {
  syncListeners.push(callback);
  return () => {
    syncListeners = syncListeners.filter(cb => cb !== callback);
  };
};

/**
 * Benachrichtigt alle Listener
 */
const notifyListeners = (event, data) => {
  syncListeners.forEach(cb => cb(event, data));
};

/**
 * Führt einen einzelnen Upload durch
 */
const executeUpload = async (upload) => {
  const { type, blob, projectId, userId, userName, caption, description, metadata } = upload;
  
  const formData = new FormData();
  
  if (type === 'photo') {
    formData.append('file', blob, `baustelle_foto_${Date.now()}.jpg`);
    
    const response = await fetch(
      `${getApiUrl()}/upload/chat?project_id=${projectId}&sender_id=${userId}&caption=${encodeURIComponent(caption || '📸 Foto vom Einsatzort (Offline-Sync)')}`,
      { method: 'POST', body: formData }
    );
    
    if (!response.ok) throw new Error(`Upload fehlgeschlagen: ${response.status}`);
    return await response.json();
  }
  
  if (type === 'voice') {
    formData.append('file', blob, `baustelle_voice_${Date.now()}.webm`);
    
    const response = await fetch(
      `${getApiUrl()}/upload/chat?project_id=${projectId}&sender_id=${userId}&caption=${encodeURIComponent(caption || '🎤 Sprachnotiz vom Einsatzort (Offline-Sync)')}`,
      { method: 'POST', body: formData }
    );
    
    if (!response.ok) throw new Error(`Upload fehlgeschlagen: ${response.status}`);
    
    const result = await response.json();
    
    // Transkription anfordern
    if (result.file_path) {
      try {
        await fetch(`${getApiUrl()}/transcribe-voice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            project_id: projectId,
            sender_id: userId,
            file_path: result.file_path,
            language: metadata?.language || 'de'
          })
        });
      } catch (e) {
        console.log('Transkription übersprungen:', e);
      }
    }
    
    return result;
  }
  
  if (type === 'defect') {
    // 1. Task ERST erstellen
    const title = description 
      ? (description.length > 50 ? description.substring(0, 50) + '...' : description)
      : 'Mangel gemeldet (Offline)';
    
    const taskData = {
      project_id: projectId,
      title: `⚠️ ${title}`,
      description: description || `Mangel gemeldet von ${userName} (Offline-Sync)`,
      priority: 'urgent',
      tags: ['Mangel', 'Einsatzort', 'Offline-Sync']
    };
    
    const taskResponse = await fetch(`${getApiUrl()}/tasks?created_by=${userId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(taskData)
    });
    
    if (!taskResponse.ok) throw new Error(`Task-Erstellung fehlgeschlagen: ${taskResponse.status}`);
    
    const createdTask = await taskResponse.json();
    const taskId = createdTask.id;
    
    // 2. Foto als Attachment an Task anhängen (wenn vorhanden)
    if (blob && taskId) {
      const photoFormData = new FormData();
      photoFormData.append('file', blob, `mangel_foto_${Date.now()}.jpg`);
      photoFormData.append('uploader_id', userId);
      photoFormData.append('attachment_type', 'task');
      
      try {
        await fetch(`${getApiUrl()}/tasks/${taskId}/attachments`, {
          method: 'POST',
          body: photoFormData
        });
      } catch (photoError) {
        console.error('Foto-Anhang fehlgeschlagen:', photoError);
      }
    }
    
    return createdTask;
  }
  
  throw new Error(`Unbekannter Upload-Typ: ${type}`);
};

/**
 * Synchronisiert alle ausstehenden Uploads
 */
export const syncPendingUploads = async () => {
  if (syncInProgress) {
    console.log('🔄 Sync bereits in Bearbeitung...');
    return { synced: 0, failed: 0 };
  }
  
  if (!navigator.onLine) {
    console.log('📴 Offline - Sync übersprungen');
    return { synced: 0, failed: 0 };
  }
  
  syncInProgress = true;
  notifyListeners('sync-start', {});
  
  let synced = 0;
  let failed = 0;
  
  try {
    const uploads = await getPendingUploads();
    console.log(`🔄 ${uploads.length} Uploads zu synchronisieren...`);
    
    for (const upload of uploads) {
      try {
        await updateUploadStatus(upload.id, 'uploading');
        notifyListeners('upload-start', { id: upload.id, type: upload.type });
        
        await executeUpload(upload);
        
        await updateUploadStatus(upload.id, 'completed');
        await deleteUpload(upload.id);
        
        synced++;
        notifyListeners('upload-complete', { id: upload.id, type: upload.type });
        console.log(`✅ Upload ${upload.id} synchronisiert`);
        
      } catch (error) {
        console.error(`❌ Upload ${upload.id} fehlgeschlagen:`, error);
        await updateUploadStatus(upload.id, 'failed', error.message);
        failed++;
        notifyListeners('upload-failed', { id: upload.id, error: error.message });
      }
    }
    
  } finally {
    syncInProgress = false;
    notifyListeners('sync-complete', { synced, failed });
  }
  
  return { synced, failed };
};

/**
 * Versucht einen einzelnen fehlgeschlagenen Upload erneut
 */
export const retryUpload = async (id) => {
  const uploads = await getAllPendingUploads();
  const upload = uploads.find(u => u.id === id);
  
  if (!upload) {
    throw new Error('Upload nicht gefunden');
  }
  
  if (!navigator.onLine) {
    throw new Error('Keine Internetverbindung');
  }
  
  try {
    await updateUploadStatus(id, 'uploading');
    await executeUpload(upload);
    await updateUploadStatus(id, 'completed');
    await deleteUpload(id);
    return true;
  } catch (error) {
    await updateUploadStatus(id, 'failed', error.message);
    throw error;
  }
};

/**
 * Registriert Online/Offline Event-Listener für automatischen Sync
 */
export const setupAutoSync = () => {
  const handleOnline = () => {
    console.log('🌐 Online - Starte automatischen Sync...');
    notifyListeners('online', {});
    // Kurze Verzögerung um sicherzustellen dass Verbindung stabil ist
    setTimeout(() => {
      syncPendingUploads();
    }, 2000);
  };
  
  const handleOffline = () => {
    console.log('📴 Offline');
    notifyListeners('offline', {});
  };
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Initial sync wenn online
  if (navigator.onLine) {
    setTimeout(() => {
      syncPendingUploads();
    }, 3000);
  }
  
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};
